import React, { useState, useEffect, useRef } from 'react';

export default function NewPlayModal({
    isOpen,
    onClose,
    onConfirm,
    availableOffenseFormations = [],
    availableDefenseFormations = [],
    currentOffenseFormation,
    currentDefenseFormation,
    existingPlayNames = []
}) {
    const [teamType, setTeamType] = useState('Offense');
    const [formation, setFormation] = useState('');
    const [playName, setPlayName] = useState('');
    const inputRef = useRef(null);

    // Auto-generate name logic
    const generateUniqueName = (formationName) => {
        if (!formationName) return "";
        let base = `${formationName} - New Play_00`;
        let counter = 0;
        let finalName = base;

        while (existingPlayNames.includes(finalName)) {
            counter++;
            const numStr = counter.toString().padStart(2, '0');
            finalName = `${formationName} - New Play_${numStr}`;
        }
        return finalName;
    };

    useEffect(() => {
        if (isOpen) {
            // Determine initial team type based on what's active or default to Offense
            const initialType = (currentDefenseFormation && !currentOffenseFormation) ? 'Defense' : 'Offense';
            setTeamType(initialType);

            const initialFormation = initialType === 'Offense'
                ? (currentOffenseFormation || availableOffenseFormations[0] || '')
                : (currentDefenseFormation || availableDefenseFormations[0] || '');

            setFormation(initialFormation);
            setPlayName(generateUniqueName(initialFormation));

            // Focus and select the input text after open
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    inputRef.current.select();
                }
            }, 100);
        }
    }, [isOpen, currentOffenseFormation, currentDefenseFormation]);

    useEffect(() => {
        if (isOpen && formation) {
            setPlayName(generateUniqueName(formation));
        }
    }, [formation]);

    if (!isOpen) return null;

    const currentFormationsList = teamType === 'Offense' ? availableOffenseFormations : availableDefenseFormations;

    const handleConfirm = () => {
        if (!playName.trim()) return alert('Please enter a play name.');
        if (!formation) return alert('Please select a formation.');
        onConfirm({ name: playName, formation, teamType });
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[110] backdrop-blur-sm">
            <div
                className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-[450px] overflow-hidden transform scale-100 transition-all border-t-4 border-t-blue-500"
                onClick={e => e.stopPropagation()}
            >
                <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-white">Create New Play</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors text-xl">✕</button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Offense/Defense Toggle */}
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Side</label>
                        <div className="flex bg-gray-900 p-1 rounded-lg border border-gray-700">
                            <button
                                className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all ${teamType === 'Offense' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200'}`}
                                onClick={() => {
                                    setTeamType('Offense');
                                    setFormation(currentOffenseFormation || availableOffenseFormations[0] || '');
                                }}
                            >
                                Offense
                            </button>
                            <button
                                className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all ${teamType === 'Defense' ? 'bg-red-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200'}`}
                                onClick={() => {
                                    setTeamType('Defense');
                                    setFormation(currentDefenseFormation || availableDefenseFormations[0] || '');
                                }}
                            >
                                Defense
                            </button>
                        </div>
                    </div>

                    {/* Formation Selection */}
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Base Formation</label>
                        <select
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-sm text-white outline-none focus:border-blue-500 transition-all cursor-pointer"
                            value={formation}
                            onChange={(e) => setFormation(e.target.value)}
                        >
                            <option value="" disabled>Choose a formation...</option>
                            {currentFormationsList.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </div>

                    {/* Play Name */}
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Play Name</label>
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Enter play name..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-sm text-white outline-none focus:border-blue-500 transition-all font-medium"
                            value={playName}
                            onChange={(e) => setPlayName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleConfirm();
                                if (e.key === 'Escape') onClose();
                            }}
                        />
                    </div>
                </div>

                <div className="px-6 py-4 bg-gray-900/50 flex justify-end gap-3 border-t border-gray-700">
                    <button
                        className="px-4 py-2 text-gray-400 hover:text-white text-xs font-bold transition-colors"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold shadow-lg shadow-blue-900/20 transition-all transform hover:scale-105 active:scale-95"
                        onClick={handleConfirm}
                    >
                        Create Play
                    </button>
                </div>
            </div>
        </div>
    );
}
